import type { ComponentProps, MutableRefObject } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { color, fontSize, fontWeight, lineHeight, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import ImageUpload from 'common/components/ImageUpload';
import ArrowButton from 'common/components/Reclamation/ArrowButton';
import { Text } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import { usePictures } from '../../hooks/additional-info/pictures';
import { dispatchAdditionalInfoClickEvent } from '../../utils/tracking';
import type { AdditionalInfoTrackingEventData } from '../../utils/tracking';

interface ReclamationStepImageUploadProps {
  reclamationId: string;
  itemId: string;
  fileInputRef: MutableRefObject<Nullable<HTMLInputElement>>;
  trackingEventData: AdditionalInfoTrackingEventData;
  onGuideButtonClick: () => void;
  onPictureCountChange: (count: number) => void;
}

const UnstyledHeading = styled.div<any>`
  ${color};
  ${fontSize};
  ${fontWeight};
  ${lineHeight};
  ${space};
`;

const Heading = (props: ComponentProps<typeof UnstyledHeading>) => (
  <UnstyledHeading
    color="sys.neutral.text.strong"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_20"
    mb="sp_4"
    {...props}
  />
);

const UnstyledDescription = styled(Text)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    fontSize="fs_16"
    mb="sp_16"
    color="sys.neutral.text.default"
    lineHeight="lh_20"
    {...props}
  />
);

const ReclamationStepImageUpload = ({
  reclamationId,
  itemId,
  fileInputRef,
  trackingEventData,
  onGuideButtonClick,
  onPictureCountChange,
}: ReclamationStepImageUploadProps) => {
  const { pictures, deletePicture, uploadPictures, fetchPictures } = usePictures(
    reclamationId,
    itemId
  );
  const { t } = useLocalization();
  const guideButtonLabel = t('h24_see_how_to_take_a_good_picture');

  useEffect(() => {
    onPictureCountChange(pictures?.length ?? 0);
  }, [pictures]);

  const handleImageDeletion = (src: string) => {
    const { pathname } = new URL(src);

    return deletePicture(pathname.replace('/reclamation/attach/', ''));
  };

  const guideButtonClickHandler = () => {
    dispatchAdditionalInfoClickEvent({
      ...trackingEventData,
      link_text: guideButtonLabel,
    });

    onGuideButtonClick();
  };

  return (
    <Flex flexDirection="column" {...(pictures?.length > 0 ? { pb: 'sp_16' } : {})}>
      <Heading>{t('h24_take_a_picture_label')}</Heading>
      <Description>{t('h24_take_a_picture_label_description')}</Description>
      <ImageUpload
        deleteImage={handleImageDeletion}
        images={pictures}
        uploadImages={uploadPictures}
        fileInputRef={fileInputRef}
        fetchPictures={fetchPictures}
      >
        <ArrowButton onClick={guideButtonClickHandler}>{guideButtonLabel}</ArrowButton>
      </ImageUpload>
    </Flex>
  );
};

export default ReclamationStepImageUpload;
